import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Upload, Trash2, Copy, Check, Image as ImageIcon, Loader2, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_DIM = 1920;
const JPEG_QUALITY = 0.85;
const SKIP_COMPRESS_BELOW = 400 * 1024;

function getImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${BACKEND_URL}${url}`;
}

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function compressImage(file) {
  return new Promise((resolve) => {
    if (file.size < SKIP_COMPRESS_BELOW) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
          else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext("2d");
        const isPng = file.type === "image/png";
        if (!isPng) { ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, width, height); }
        ctx.drawImage(img, 0, 0, width, height);
        const outputType = isPng ? "image/png" : "image/jpeg";
        const quality = isPng ? undefined : JPEG_QUALITY;
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          const ext = isPng ? ".png" : ".jpg";
          const name = file.name.replace(/\.[^.]+$/, ext);
          resolve(new File([blob], name, { type: outputType }));
        }, outputType, quality);
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export default function MediaManager() {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const fetchImages = () =>
    axios.get(`${API}/media`, { withCredentials: true })
      .then(r => setImages(r.data))
      .catch(() => {});

  useEffect(() => { fetchImages(); }, []);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true); setError("");
    const fileList = Array.from(files);
    let uploaded = 0;
    try {
      for (const file of fileList) {
        setUploadProgress(`Procesando ${uploaded + 1}/${fileList.length}: ${file.name}`);
        const compressed = await compressImage(file);
        const sizeDiff = file.size > compressed.size ? ` → ${formatSize(compressed.size)}` : "";
        setUploadProgress(`Subiendo ${uploaded + 1}/${fileList.length}${sizeDiff}...`);
        const fd = new FormData();
        fd.append("file", compressed);
        await axios.post(`${API}/media/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        uploaded++;
      }
      await fetchImages();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al subir la imagen. Comprueba el tamaño y formato.");
    } finally {
      setUploading(false); setUploadProgress("");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    try {
      await axios.delete(`${API}/media/${id}`, { withCredentials: true });
      await fetchImages();
    } catch { setError("Error al eliminar la imagen."); }
  };

  const handleToggleGallery = async (img) => {
    try {
      const res = await axios.patch(`${API}/media/${img._id}/gallery`, {}, { withCredentials: true });
      setImages(prev => prev.map(m => m._id === img._id ? { ...m, en_galeria: res.data.en_galeria } : m));
    } catch { setError("Error al actualizar la visibilidad."); }
  };

  const handleCopy = (url) => {
    const fullUrl = getImageUrl(url);
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopiedId(url); setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files);
  };

  const galleryCount = images.filter(i => i.en_galeria !== false).length;

  return (
    <div data-testid="media-manager">
      <div className="mb-6">
        <h1 className="font-['Montserrat'] font-bold text-2xl text-[#0F172A] mb-1">Galería de Imágenes</h1>
        <p className="text-[#475569] font-['Montserrat'] text-sm">
          Sube fotos del campus. Las imágenes se comprimen automáticamente.
          Usa el ojo <Eye size={13} className="inline" /> para mostrar/ocultar fotos en la sección de galería de la web.
        </p>
        {galleryCount > 0 && (
          <p className="mt-1 font-['Montserrat'] text-xs" style={{ color: "#0072A4" }}>
            {galleryCount} imagen{galleryCount > 1 ? "es" : ""} visible{galleryCount > 1 ? "s" : ""} en la landing page
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mb-4" data-testid="media-error">
          <p className="font-['Montserrat'] text-red-600 text-sm flex-1">{error}</p>
          <button onClick={() => setError("")} className="text-red-400 hover:text-red-600 text-xs">✕</button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        data-testid="upload-zone"
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer mb-8 transition-all ${dragOver ? "border-[#0072A4] bg-[#E0F0FF]" : "border-[#E2E8F0] bg-white hover:border-[#0072A4] hover:bg-[#F0F8FF]"} ${uploading ? "cursor-wait opacity-75" : ""}`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={36} color="#0072A4" className="animate-spin" />
            <p className="font-['Montserrat'] font-semibold text-[#0072A4] text-sm">{uploadProgress || "Subiendo..."}</p>
          </div>
        ) : (
          <>
            <Upload size={36} color="#94A3B8" className="mx-auto mb-3" />
            <p className="font-['Montserrat'] font-semibold text-[#0F172A] mb-1">Arrastra imágenes o haz clic para seleccionar</p>
            <p className="font-['Montserrat'] text-[#94A3B8] text-sm">PNG, JPG, WEBP — Máx 1920px, compresión automática</p>
          </>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={e => handleUpload(e.target.files)} className="hidden" data-testid="media-file-input" />
      </div>

      {/* Image grid */}
      {images.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-[#E2E8F0]">
          <ImageIcon size={40} color="#E2E8F0" className="mx-auto mb-3" />
          <p className="font-['Montserrat'] text-[#94A3B8] text-sm">No hay imágenes todavía</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {images.map((img) => {
            const inGallery = img.en_galeria !== false;
            return (
              <div key={img._id} data-testid={`media-item-${img._id}`}
                className={`bg-white rounded-xl border overflow-hidden hover:shadow-md transition-shadow group ${inGallery ? "border-[#E2E8F0]" : "border-[#E2E8F0] opacity-60"}`}>
                <div className="relative overflow-hidden bg-[#F8FAFC]" style={{ aspectRatio: "1/1" }}>
                  <img
                    src={getImageUrl(img.url)}
                    alt={img.original_name}
                    className="w-full h-full object-contain p-1"
                    onError={e => { e.target.style.display = "none"; }}
                  />
                  {/* Action overlay on hover */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button onClick={() => handleCopy(img.url)} data-testid={`copy-url-${img._id}`}
                      className="p-2 bg-white rounded-lg hover:bg-[#F0F8FF] transition-colors" title="Copiar URL">
                      {copiedId === img.url ? <Check size={14} color="#3CB44B" /> : <Copy size={14} color="#0072A4" />}
                    </button>
                    <button onClick={() => handleToggleGallery(img)} data-testid={`toggle-gallery-${img._id}`}
                      className="p-2 bg-white rounded-lg hover:bg-[#F0F8FF] transition-colors"
                      title={inGallery ? "Ocultar de la galería web" : "Mostrar en la galería web"}>
                      {inGallery ? <Eye size={14} color="#0072A4" /> : <EyeOff size={14} color="#94A3B8" />}
                    </button>
                    <button onClick={() => handleDelete(img._id)} data-testid={`delete-media-${img._id}`}
                      className="p-2 bg-white rounded-lg hover:bg-red-50 transition-colors" title="Eliminar">
                      <Trash2 size={14} color="#EF4444" />
                    </button>
                  </div>
                  {/* Gallery status badge */}
                  {!inGallery && (
                    <div className="absolute top-2 left-2 bg-black/60 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <EyeOff size={10} color="rgba(255,255,255,0.7)" />
                      <span className="text-white/70 font-['Montserrat'] text-xs">Oculta</span>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <p className="font-['Montserrat'] text-xs text-[#475569] truncate" title={img.original_name}>{img.original_name}</p>
                  {img.size && <p className="font-['Montserrat'] text-xs text-[#94A3B8]">{formatSize(img.size)}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
